/**
 * SpliceDetector — multi-signal audio splice detection.
 *
 * Designed as a *forensic-grade* alternative to the single-signal spectral-flux
 * check used by audio-forensics. A real-world splice (an audio cut joining two
 * different sources, or a cut within the same source after editing) leaves
 * traces in *several* independent domains. Looking at only one is brittle —
 * spectral flux false-positives on natural percussive transients (drums, claps,
 * gunshots) and false-negatives on splices between similar segments.
 *
 * This engine extracts 8 complementary signals and fuses them with weights:
 *
 *   1. Spectral flux discontinuity   — STFT frame-to-frame energy change
 *   2. Noise-floor change-point      — RMS of low-energy quartile per window
 *   3. Sample-level click            — broadband transient at the seam
 *   4. ENF (50/60 Hz) frequency+phase break — Grigoras 2007. Tracks both
 *                                      sub-Hz frequency drift AND phase
 *                                      residual; splices break either or both.
 *   5. Pitch (F0) jump               — voice splices break local pitch
 *   6. MFCC distance                 — local timbre change
 *   7. Bicoherence (HOS, third-order)— non-Gaussian transients at the seam.
 *                                      Splice transients leak phase coupling
 *                                      between frequency components, which
 *                                      bicoherence makes quantifiable. Catches
 *                                      what spectral flux misses on smooth
 *                                      crossfades whose spectrum-magnitude
 *                                      changes are subtle.
 *   8. RIR (room impulse) discontinuity — late-energy ratio per onset. Same
 *                                      mic / different room → step change in
 *                                      reverberance. Closes the gap that
 *                                      noise-floor + MFCC leave on identical-
 *                                      gain splices between two rooms.
 *
 * Per-signal scores are computed on a uniform 50 ms time grid (`bucketMs=50`),
 * weighted by reliability (ENF and noise-floor are stronger forensic evidence
 * than spectral flux), summed, and thresholded to extract splice candidates.
 * Adjacent candidates within `mergeMs` (default 250 ms) collapse into one.
 *
 * Public API:
 *   await SpliceDetector.analyze(audioBuffer, opts)
 *     audioBuffer: Web Audio AudioBuffer (mono is taken from channel 0)
 *     opts: { onProgress?: (stage, pct) => void, mergeMs?: 250, bucketMs?: 50,
 *             minScore?: 0.55, sigmaK?: { flux: 3.0, noise: 2.5,
 *                                         click: 4.0, pitch: 3.0, mfcc: 3.0 } }
 *   →  Promise<{
 *        splices: [{ time, score, sigSig: {flux,noise,click,enf,pitch,mfcc},
 *                    explain: string[] }],
 *        signals: { flux, noise, click, enf, pitch, mfcc, fused }, // per-bucket arrays
 *        bucketMs, duration, sampleRate,
 *        verdict: 'clean' | 'one_splice' | 'multiple_splices' | 'tampered',
 *        rawScore, score,                                          // 0-100
 *        thresholds,                                               // for UI
 *      }>
 *
 * The engine is pure functions + a single public method on `window.SpliceDetector`.
 * It uses no FFmpeg, no WASM, no workers — runs in 200-1500 ms on typical
 * inputs, which is fast enough to render the timeline immediately after upload.
 *
 * Design choices:
 *   - All thresholds are *μ + k·σ* relative to the file's own statistics, not
 *     fixed numerical cuts. This makes the detector self-calibrating across
 *     loud/quiet sources.
 *   - Long files (>120 s) are processed at 22050 Hz to keep FFT cost bounded.
 *   - Mono-mix of stereo inputs uses (L+R)/2 — preserves splice signature even
 *     when one channel is louder than the other.
 *   - We deliberately do NOT claim statistical confidence. This is a heuristic
 *     score with disclosed methodology — see audio-forensics for the same
 *     stance and the lessons that drove it (Heuristic score, not "Confidence").
 */
(function() {
    'use strict';

    // Engine version. Bump when algorithm changes — clients invalidate their
    // cached self-validation results in localStorage when this changes.
    var __VERSION = '2.6.0';

    var DEFAULTS = {
        bucketMs: 50,
        mergeMs: 250,
        // Calibrated against the in-browser self-validator. 0.50 corresponds
        // to "≥3 saturated signals fire OR several mid-level signals plus
        // neighborhood corroboration". Lower values raise sensitivity at the
        // cost of false positives. Robust normalization handles per-signal
        // outliers; minRawScore gates against pooling-driven false positives
        // in clean files with natural fluctuations.
        minScore: 0.50,
        // Raw-fused gate: a bucket only qualifies if its un-pooled fused
        // score is at least 60% of minScore. Prevents 5 mild-firing buckets
        // from combining into a spurious "splice" via the pooling boost.
        minRawScoreFraction: 0.60,
        sigmaK: {
            flux: 3.0, noise: 3.5, click: 4.0, pitch: 3.0,
            mfcc: 3.0, bicoh: 2.8, rir: 2.5,
        },
        // Weights — calibrated against the in-browser self-validator's
        // 17-scenario battery. ENF stays gold-standard when present; the
        // other weights reflect real firing rates: flux + noise + mfcc are
        // the most reliable "any-splice" signals, click fires only on
        // no-crossfade cuts, bicoh only on non-Gaussian transients, RIR
        // only on room changes. Sum = 1.0; redistribution kicks in when
        // ENF or RIR are absent.
        weights: {
            flux:  0.18, noise: 0.20, click: 0.10,
            enf:   0.18, pitch: 0.10, mfcc: 0.14,
            bicoh: 0.06, rir:   0.04,
        },
        targetSampleRate: 22050,    // resample threshold for analysis
        // Hard cap on file duration we'll analyze. Files above this cap are
        // vanishingly rare in the audio-forensics use case (interviews,
        // podcasts, voice notes, recorded conversations).
        maxAnalyzeSec: 1800,        // 30 min
    };

    /* ──────────────────────────────────────────────────────────────────────
     * Helpers — windowing, FFT, downmix
     * ────────────────────────────────────────────────────────────────────── */

    /* ──────────────────────────────────────────────────────────────────────
     * Content classifier — music vs speech.
     *
     * The 8-signal detector is calibrated for SPEECH content (interviews,
     * podcasts, voice notes, recorded conversations). On music tracks the
     * natural transients (drum hits, instrument attacks, beat changes,
     * section boundaries) trigger flux/click/MFCC/pitch signals at every
     * structural event — producing dozens of "splice candidates" that are
     * actually just normal music.
     *
     * We classify content type via two cheap heuristics:
     *   - Crest factor:  peak / RMS. Speech ≈ 5–8; pop music compressed
     *     to ≈ 3–5 (heavy limiting); classical / acoustic ≈ 6–10.
     *   - Spectral flatness: Wiener entropy (geometric mean / arithmetic
     *     mean of band energies). Speech ≈ 0.2–0.4, music broadband ≈
     *     0.4–0.6, music tonal ≈ 0.05–0.15.
     *   - Harmonic stability: ratio of frame-to-frame harmonic energy
     *     persistence. Music has sustained notes / chords spanning many
     *     frames; speech is more transient.
     *
     * Classification: if any TWO of three indicate music, set
     * isMusicLike=true. The engine then doubles its sigmaK for flux,
     * click and pitch (signals that fire on music transients) and
     * surfaces the flag in the result so the UI can show a banner.
     * ────────────────────────────────────────────────────────────────────── */
    function classifyContent(samples, sr) {
        var n = samples.length;
        if (n < sr * 0.5) return { isMusicLike: false, crest: 0, flatness: 0, harmonicity: 0 };

        // 1. Crest factor (RMS-based)
        var sumSq = 0;
        var peak = 0;
        for (var i = 0; i < n; i++) {
            var v = samples[i] < 0 ? -samples[i] : samples[i];
            sumSq += v * v;
            if (v > peak) peak = v;
        }
        var rms = Math.sqrt(sumSq / n);
        var crest = rms > 1e-9 ? peak / rms : 0;
        var lowCrest = crest > 0 && crest < 4.5;     // heavy compression → music

        // 2. Spectral flatness (Wiener entropy) on a single mid-file 1-s
        //    chunk via FFT magnitude.
        var fftSize = 4096;
        var midOff = Math.max(0, Math.floor((n - fftSize) / 2));
        var win = hann(fftSize);
        var re = new Float32Array(fftSize), im = new Float32Array(fftSize);
        for (var i = 0; i < fftSize; i++) {
            re[i] = (midOff + i < n ? samples[midOff + i] : 0) * win[i];
            im[i] = 0;
        }
        fft(re, im);
        var nBins = fftSize >> 1;
        // Skip DC and very-low bins (mains hum).
        var binLo = Math.max(4, Math.round(120 / sr * fftSize));
        var binHi = Math.min(nBins - 1, Math.round(8000 / sr * fftSize));
        var logSum = 0, linSum = 0, count = 0;
        for (var b = binLo; b <= binHi; b++) {
            var p = re[b] * re[b] + im[b] * im[b] + 1e-12;
            logSum += Math.log(p);
            linSum += p;
            count++;
        }
        var flatness = count > 0 ? Math.exp(logSum / count) / (linSum / count) : 0;
        // Music broadband → mid; tonal → very low; speech → low-mid
        // The discriminating range that signals MUSIC is "broadband" (mix,
        // pop): flatness > 0.35 OR very tonal (sustained chord): < 0.08
        // strictly OK that's still music. So music indicator = flatness
        // outside the 0.10–0.30 speech window.
        var musicalFlatness = (flatness > 0.30) || (flatness > 0 && flatness < 0.08);

        // 3. Harmonic stability via short-time autocorrelation persistence:
        //    measure how strongly the dominant lag in window w correlates
        //    with the dominant lag in window w-1. Music has sustained
        //    notes/chords (high persistence); speech is transient.
        var winSec = 0.05, winSize = Math.floor(sr * winSec), hop = winSize;
        var nWin = Math.floor((n - winSize) / hop);
        if (nWin < 8) return { isMusicLike: lowCrest && musicalFlatness, crest: crest, flatness: flatness, harmonicity: 0 };
        var lagMin = Math.max(2, Math.floor(sr / 600));
        var lagMax = Math.min(winSize - 1, Math.floor(sr / 80));
        var lastLag = -1, persisted = 0, voicedWins = 0;
        for (var w = 0; w < nWin; w++) {
            var off = w * hop;
            var ssq = 0;
            for (var i = 0; i < winSize; i++) ssq += samples[off + i] * samples[off + i];
            var winRms = Math.sqrt(ssq / winSize);
            if (winRms < 0.005) { lastLag = -1; continue; }
            var bestLag = 0, bestR = 0;
            for (var lag = lagMin; lag <= lagMax; lag++) {
                var r = 0;
                for (var i = 0; i + lag < winSize; i++) r += samples[off + i] * samples[off + i + lag];
                r /= ssq + 1e-9;
                if (r > bestR) { bestR = r; bestLag = lag; }
            }
            if (bestR < 0.4) { lastLag = -1; continue; }
            voicedWins++;
            if (lastLag > 0 && Math.abs(bestLag - lastLag) <= 2) persisted++;
            lastLag = bestLag;
        }
        var harmonicity = voicedWins > 0 ? persisted / voicedWins : 0;
        // Speech has F0 glide → low harmonicity (~0.3-0.5)
        // Music has sustained notes → high harmonicity (~0.6-0.95)
        var musicalHarmonicity = harmonicity > 0.55 && voicedWins > 10;

        // Two-of-three vote for music classification
        var votes = (lowCrest ? 1 : 0) + (musicalFlatness ? 1 : 0) + (musicalHarmonicity ? 1 : 0);
        return {
            isMusicLike: votes >= 2,
            crest: +crest.toFixed(2),
            flatness: +flatness.toFixed(3),
            harmonicity: +harmonicity.toFixed(2),
            votes: votes,
        };
    }

    function downmixToMono(buf) {
        var ch0 = buf.getChannelData(0);
        if (buf.numberOfChannels === 1) return ch0;
        var ch1 = buf.getChannelData(1);
        var out = new Float32Array(ch0.length);
        for (var i = 0; i < ch0.length; i++) out[i] = (ch0[i] + ch1[i]) * 0.5;
        return out;
    }

    function resampleLinear(samples, srIn, srOut) {
        if (srIn === srOut) return samples;
        var ratio = srIn / srOut;
        var nOut = Math.floor(samples.length / ratio);
        var out = new Float32Array(nOut);
        for (var i = 0; i < nOut; i++) {
            var pos = i * ratio;
            var lo = pos | 0;
            var frac = pos - lo;
            var hi = lo + 1 < samples.length ? lo + 1 : lo;
            out[i] = samples[lo] * (1 - frac) + samples[hi] * frac;
        }
        return out;
    }

    // 2026-06-04 DSP fix: Kaiser-windowed sinc resampler. The previous
    // resampleLinear at the analysis downsample (44.1↔22.05k) had no LPF —
    // frequencies above 11025 Hz folded back into the analysis band and
    // caused spurious spectral-flux events → false-positive splice claims.
    // Kaiser sinc bakes the LP filter into the resample. See
    // [[reference_oac_not_hq_resampler]] for why we don't route through
    // OfflineAudioContext (Chromium OAC == linear quality, not HQ).
    async function resampleHQ(src, srIn, srOut) {
        if (srIn === srOut) return new Float32Array(src);
        var TAPS = 24, BETA = 8.6;
        var ratio = srIn / srOut;
        var lpCutoff = 0.5 / Math.max(ratio, 1.0);
        function bessI0(x){
            var y = (x / 2) * (x / 2), s = 1, a = 1;
            for (var i = 1; i < 30; i++) { a *= y / (i * i); s += a; if (a < 1e-15) break; }
            return s;
        }
        var I0Beta = bessI0(BETA);
        var dstLen = Math.round(src.length / ratio);
        var out = new Float32Array(dstLen);
        var srcLen = src.length;

        /* ⚠️ Здесь коэффициенты пересчитывались ДЛЯ КАЖДОГО выходного сэмпла и
           каждого из 48 отводов: синус, корень и функция Бесселя циклом до 30
           итераций. Замер: 8.4 с на 20 с звука — 94% ВСЕГО анализа, больше чем
           все восемь детекторов вместе (0.9 с). Отсюда десятиминутное ожидание
           у пользователей (proc_sec 601 на файл 230 с).

           Но коэффициенты зависят ТОЛЬКО от дробной части позиции: x = k − frac.
           У целых частот дискретизации frac повторяется с периодом
           srOut/НОД(srIn,srOut) — для 44.1→22.05 это одно значение, для
           48→22.05 сто сорок семь. Кешируем набор отводов по frac: выход
           побитово тот же, счёт в разы дешевле. Если период вдруг велик
           (нецелые частоты), кеш не растёт — считаем по-старому. */
        var W = 2 * TAPS;               // отводов на выборку
        var cache = new Map(), CACHE_MAX = 4096;

        function tapsFor(frac) {
            var key = Math.round(frac * 1e9);
            var got = cache.get(key);
            if (got) return got;
            var c = new Float64Array(W);
            for (var k = -TAPS + 1, idx = 0; k <= TAPS; k++, idx++) {
                var x = k - frac;
                var arg = 2 * Math.PI * lpCutoff * x;
                var sinc = (Math.abs(x) < 1e-9) ? 1 : Math.sin(arg) / arg;
                var t = x / TAPS;
                var w = (Math.abs(t) >= 1) ? 0 : bessI0(BETA * Math.sqrt(1 - t * t)) / I0Beta;
                c[idx] = sinc * w * (2 * lpCutoff);
            }
            if (cache.size < CACHE_MAX) cache.set(key, c);
            return c;
        }

        for (var i = 0; i < dstLen; i++) {
            var center = i * ratio;
            var i0 = Math.floor(center);
            var c = tapsFor(center - i0);
            var acc = 0;
            for (var k = -TAPS + 1, idx = 0; k <= TAPS; k++, idx++) {
                var n = i0 + k;
                if (n < 0 || n >= srcLen) continue;
                acc += src[n] * c[idx];
            }
            out[i] = acc;
        }
        return out;
    }

    var _hannCache = {};
    function hann(n) {
        if (_hannCache[n]) return _hannCache[n];
        var w = new Float32Array(n);
        for (var i = 0; i < n; i++) w[i] = 0.5 - 0.5 * Math.cos(2 * Math.PI * i / (n - 1));
        return _hannCache[n] = w;
    }

    // Radix-2 in-place FFT.
    function fft(re, im) {
        var n = re.length;
        for (var i = 1, j = 0; i < n; i++) {
            var bit = n >> 1;
            for (; j & bit; bit >>= 1) j ^= bit;
            j ^= bit;
            if (i < j) {
                var tr = re[i]; re[i] = re[j]; re[j] = tr;
                var ti = im[i]; im[i] = im[j]; im[j] = ti;
            }
        }
        for (var len = 2; len <= n; len <<= 1) {
            var ang = -2 * Math.PI / len;
            var wlr = Math.cos(ang), wli = Math.sin(ang);
            for (var i2 = 0; i2 < n; i2 += len) {
                var wr = 1, wi = 0;
                for (var k = 0; k < (len >> 1); k++) {
                    var a = i2 + k, b = i2 + k + (len >> 1);
                    var tre = wr * re[b] - wi * im[b];
                    var tim = wr * im[b] + wi * re[b];
                    re[b] = re[a] - tre; im[b] = im[a] - tim;
                    re[a] += tre;        im[a] += tim;
                    var nwr = wr * wlr - wi * wli;
                    wi = wr * wli + wi * wlr;
                    wr = nwr;
                }
            }
        }
    }

    function meanStd(arr) {
        var n = arr.length;
        if (n === 0) return { mean: 0, std: 0 };
        var m = 0;
        for (var i = 0; i < n; i++) m += arr[i];
        m /= n;
        var v = 0;
        for (var i = 0; i < n; i++) { var d = arr[i] - m; v += d * d; }
        return { mean: m, std: Math.sqrt(v / n) };
    }

    /* ──────────────────────────────────────────────────────────────────────
     * Signal 1 — Spectral flux on STFT frames
     *
     * For each frame k, flux[k] = sqrt(Σ max(0, |X[k,b]| - |X[k-1,b]|)²).
     * Resampled onto the bucket grid.
     * ────────────────────────────────────────────────────────────────────── */
    function computeSpectralFlux(samples, sr, bucketSamples) {
        var fftSize = 1024;
        var hop = 256;
        var numFrames = Math.floor((samples.length - fftSize) / hop);
        if (numFrames < 4) return new Float32Array(Math.ceil(samples.length / bucketSamples));

        var win = hann(fftSize);
        var prevMag = new Float32Array(fftSize / 2);
        var re = new Float32Array(fftSize);
        var im = new Float32Array(fftSize);
        var frameFlux = new Float32Array(numFrames);

        for (var k = 0; k < numFrames; k++) {
            var off = k * hop;
            for (var i = 0; i < fftSize; i++) {
                re[i] = samples[off + i] * win[i];
                im[i] = 0;
            }
            fft(re, im);
            var flux = 0;
            for (var b = 0; b < fftSize / 2; b++) {
                var mag = Math.sqrt(re[b] * re[b] + im[b] * im[b]);
                if (k > 0) {
                    var d = mag - prevMag[b];
                    if (d > 0) flux += d * d;
                }
                prevMag[b] = mag;
            }
            frameFlux[k] = Math.sqrt(flux);
        }

        // Map onto the bucket grid: each bucket takes the max flux of any
        // frame whose center falls inside it. Max (not mean) preserves the
        // peakedness of a splice transient.
        var nBuckets = Math.ceil(samples.length / bucketSamples);
        var out = new Float32Array(nBuckets);
        for (var k = 0; k < numFrames; k++) {
            var center = k * hop + fftSize / 2;
            var bi = Math.floor(center / bucketSamples);
            if (bi >= nBuckets) bi = nBuckets - 1;
            if (frameFlux[k] > out[bi]) out[bi] = frameFlux[k];
        }
        return out;
    }

    /* ──────────────────────────────────────────────────────────────────────
     * Signal 2 — Noise floor change-point detection
     *
     * Splice between two recordings made on different mics or at different
     * gain shows up as a step-change in the noise floor. We compute the
     * 10th-percentile RMS of each 200 ms window, then look for *fast* changes
     * in that envelope. This is robust to speech/music in the foreground —
     * the 10th percentile is dominated by the quietest frames within the
     * window, which is the noise floor.
     * ────────────────────────────────────────────────────────────────────── */
    function computeNoiseFloor(samples, sr, bucketSamples) {
        var winSec = 0.2;
        var winSize = Math.floor(sr * winSec);
        var hop = winSize >> 1;
        var subSize = Math.floor(winSize / 10); // 20 ms sub-frames
        if (subSize < 32) subSize = 32;
        var nWin = Math.max(0, Math.floor((samples.length - winSize) / hop));
        var floor = new Float32Array(nWin);
        var subRms = [];

        for (var w = 0; w < nWin; w++) {
            var off = w * hop;
            subRms.length = 0;
            for (var s = 0; s + subSize <= winSize; s += subSize) {
                var sum = 0;
                for (var i = 0; i < subSize; i++) {
                    var v = samples[off + s + i];
                    sum += v * v;
                }
                subRms.push(Math.sqrt(sum / subSize));
            }
            subRms.sort(function(a, b) { return a - b; });
            // 10th percentile — robust noise-floor estimate.
            var idx = Math.floor(subRms.length * 0.1);
            floor[w] = subRms[idx] || 0;
        }

        // Score per window = log-ratio of *5-tap median(past) vs 5-tap
        // median(future)*. A step change at the seam shows up as
        // log(post-floor / pre-floor) = clear positive; within-section
        // random fluctuations cancel out across the medians, so the
        // signal stays quiet between splices. This is a longer-context
        // change-point detector — the v2.1 single-window ratio fired
        // false positives on Gaussian-noise files because per-window
        // random variance was comparable to the step at the seam.
        var changes = new Float32Array(nWin);
        for (var w = 2; w < nWin - 2; w++) {
            // 3-tap median of past (w-2, w-1, w) and future (w, w+1, w+2)
            // intentionally overlap on w so a 1-window splice still
            // pushes both medians apart.
            var arrPast = [floor[w - 2], floor[w - 1], floor[w]];
            var arrNext = [floor[w], floor[w + 1], floor[w + 2]];
            arrPast.sort(function(a, b) { return a - b; });
            arrNext.sort(function(a, b) { return a - b; });
            var medPrev = arrPast[1] + 1e-9;
            var medNext = arrNext[1] + 1e-9;
            changes[w] = Math.abs(Math.log(medNext / medPrev));
        }

        // Map to bucket grid.
        var nBuckets = Math.ceil(samples.length / bucketSamples);
        var out = new Float32Array(nBuckets);
        for (var w = 0; w < nWin; w++) {
            var center = w * hop + winSize / 2;
            var bi = Math.floor(center / bucketSamples);
            if (bi >= nBuckets) bi = nBuckets - 1;
            if (changes[w] > out[bi]) out[bi] = changes[w];
        }
        return out;
    }

    /* ──────────────────────────────────────────────────────────────────────
     * Signal 3 — Sample-level click detection
     *
     * A splice without a proper crossfade leaves a 1-2 sample discontinuity
     * that produces a broadband click. We highpass the signal (1st-order
     * derivative ≈ HPF), then look for samples that exceed a local-median ×
     * sigmaK threshold. Local — not global — so the detector adapts to
     * dynamics within the file.
     * ────────────────────────────────────────────────────────────────────── */
    function computeClicks(samples, sr, bucketSamples) {
        var nBuckets = Math.ceil(samples.length / bucketSamples);
        var out = new Float32Array(nBuckets);
        if (samples.length < 4) return out;

        // Per-bucket: max |Δ²x| (second-difference) divided by local RMS.
        // |Δ²x| spikes hard at sample-discontinuities, much harder than plain
        // sample magnitude.
        for (var bi = 0; bi < nBuckets; bi++) {
            var start = bi * bucketSamples;
            var end = Math.min(samples.length - 2, start + bucketSamples);
            var maxD2 = 0;
            var sumSq = 0;
            var n = 0;
            for (var i = start; i < end; i++) {
                var v = samples[i];
                sumSq += v * v;
                n++;
                if (i + 2 < samples.length) {
                    var d2 = Math.abs(samples[i + 2] - 2 * samples[i + 1] + samples[i]);
                    if (d2 > maxD2) maxD2 = d2;
                }
            }
            var rms = n > 0 ? Math.sqrt(sumSq / n) : 0;
            // Score: how many local-RMS units does the second-derivative spike
            // sit above. Below ~6 it's just bright sound; above that it starts
            // looking like a discontinuity.
            out[bi] = rms > 1e-6 ? maxD2 / rms : 0;
        }
        return out;
    }

    /* ──────────────────────────────────────────────────────────────────────
     * Signal 4 — ENF (Electric Network Frequency) tracking
     *
     * Mains hum at 50 Hz (most of the world) or 60 Hz (Americas, parts of Asia)
     * leaks into nearly every recording made indoors or near electrical
     * equipment. In the ENF Criterion forensic literature (Grigoras, 2007)
     * two complementary quantities are tracked:
     *
     *   (a) The instantaneous *phase* of the demodulated mains tone — splices
     *       between two recordings produce a phase step even when the
     *       frequency is unchanged (they were sampled at different absolute
     *       times relative to the mains cycle).
     *
     *   (b) The instantaneous *frequency* of the mains — real grids drift
     *       slowly (±0.1 Hz over minutes due to load changes) and that drift
     *       is the same everywhere on the grid at a given moment. A splice
     *       between two recordings made at different times produces a step
     *       in the frequency trajectory which is essentially impossible to
     *       reproduce naturally.
     *
     * We track BOTH and take the per-bucket max of (a) and (b). Frequency
     * tracking uses parabolic interpolation of three FFT bins around the
     * peak in the [49.5, 50.5] (or [59.5, 60.5]) Hz band — gives sub-Hz
     * precision without the cost of a high-resolution FFT.
     *
     * If no significant ENF energy is detected, both signals are zero and
     * the ENF weight is redistributed across the remaining detectors.
     * ────────────────────────────────────────────────────────────────────── */
    function computeENF(samples, sr, bucketSamples) {
        var nBuckets = Math.ceil(samples.length / bucketSamples);
        var out = new Float32Array(nBuckets);
        out.__present = false;
        out.__freq = 0;

        var hopSec = 0.1;
        var winSec = 0.5;
        var winSize = Math.floor(sr * winSec);
        var hop = Math.floor(sr * hopSec);
        var nWin = Math.floor((samples.length - winSize) / hop);
        if (nWin < 8) return out;

        // (a) Quadrature detector at fixed f0: I/Q → phase, magnitude.
        function trackPhase(freq) {
            var phases = new Float32Array(nWin);
            var mags = new Float32Array(nWin);
            var win = hann(winSize);
            /* ⚠️ Здесь стояли Math.cos/Math.sin ДВАЖДЫ НА КАЖДЫЙ СЭМПЛ от
               абсолютного времени. У получаса записи аргумент доходит до
               миллиона радиан, и движок уходит в медленное приведение
               аргумента. Замер: этот детектор стоил 110 с на 60 с звука —
               в 17 раз дороже MFCC и практически всё время анализа (у живых
               пользователей proc_sec 601 на файл 230 с).

               Заменено вращением единичного вектора: два умножения на сэмпл
               вместо двух трансцендентных вызовов. Фаза заводится заново в
               каждом окне (0.5 с, 11 тысяч шагов), поэтому накопления ошибки
               нет — сравнение с прежним кодом побитово в пределах 1e-9. */
            var dAng = 2 * Math.PI * freq / sr;
            var cd = Math.cos(dAng), sd = Math.sin(dAng);
            for (var w = 0; w < nWin; w++) {
                var off = w * hop;
                var I = 0, Q = 0;
                var a0 = 2 * Math.PI * freq * (off / sr);
                var cr = Math.cos(a0), ci = Math.sin(a0);
                for (var i = 0; i < winSize; i++) {
                    var x = samples[off + i] * win[i];
                    I += x * cr;
                    Q += x * ci;
                    var nr = cr * cd - ci * sd;
                    ci = cr * sd + ci * cd;
                    cr = nr;
                }
                phases[w] = Math.atan2(Q, I);
                mags[w] = Math.sqrt(I * I + Q * Q);
            }
            return { phases: phases, mags: mags };
        }

        var t50 = trackPhase(50);
        var t60 = trackPhase(60);
        var pow50 = 0, pow60 = 0;
        for (var i = 0; i < t50.mags.length; i++) {
            pow50 += t50.mags[i] * t50.mags[i];
            pow60 += t60.mags[i] * t60.mags[i];
        }
        var freqNominal, phases, mags;
        if (pow50 > pow60) { freqNominal = 50; phases = t50.phases; mags = t50.mags; }
        else               { freqNominal = 60; phases = t60.phases; mags = t60.mags; }

        // Presence test — must be a clearly *narrow-band* tone at the chosen
        // mains frequency, not just FFT leakage from broadband content. We
        // require BOTH:
        //   (a) chosen-freq mean magnitude is high relative to mags' RMS
        //       across all windows (steady tone, low CV)
        //   (b) the chosen-freq integrated power is at least 5% of broadband
        //       sample power per window (lifted from v1; the v2.0 0.1%
        //       threshold falsely flagged white-noise + sine as ENF-present).
        var avgPow = 0;
        for (var i = 0; i < samples.length; i++) avgPow += samples[i] * samples[i];
        avgPow /= samples.length || 1;
        var meanMag = 0;
        for (var i = 0; i < mags.length; i++) meanMag += mags[i];
        meanMag /= mags.length || 1;
        // Per-window narrow-band power vs. per-window broadband power.
        var perWinBroadband = 0;
        for (var w = 0; w < nWin; w++) {
            for (var i = 0; i < winSize; i++) {
                var v = samples[w * hop + i];
                perWinBroadband += v * v;
            }
        }
        perWinBroadband /= (nWin * winSize) || 1;
        var enfPower = (meanMag * meanMag) / (winSize * winSize);
        var ratio = enfPower / (perWinBroadband + 1e-12);
        var magCv = 0;
        if (meanMag > 0) {
            var sq = 0;
            for (var i = 0; i < mags.length; i++) { var d = mags[i] - meanMag; sq += d * d; }
            magCv = Math.sqrt(sq / mags.length) / meanMag;
        }
        var present = ratio > 0.05 && meanMag > 1e-3 && magCv < 0.5;
        if (!present) return out;
        out.__present = true;
        out.__freq = freqNominal;

        // (a) Phase residual after detrending (existing logic).
        var unwrapped = new Float32Array(phases.length);
        unwrapped[0] = phases[0];
        for (var w = 1; w < phases.length; w++) {
            var d = phases[w] - phases[w - 1];
            while (d >  Math.PI) d -= 2 * Math.PI;
            while (d < -Math.PI) d += 2 * Math.PI;
            unwrapped[w] = unwrapped[w - 1] + d;
        }
        var n = unwrapped.length;
        var sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
        for (var w = 0; w < n; w++) { sumX += w; sumY += unwrapped[w]; sumXY += w * unwrapped[w]; sumXX += w * w; }
        var aLin = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX || 1);
        var bLin = (sumY - aLin * sumX) / n;
        var residual = new Float32Array(n);
        for (var w = 0; w < n; w++) residual[w] = unwrapped[w] - (aLin * w + bLin);
        var phaseScore = new Float32Array(n);
        for (var w = 1; w < n; w++) phaseScore[w] = Math.abs(residual[w] - residual[w - 1]);

        // (b) Per-window ENF *frequency* via parabolic interpolation of FFT
        // peak in a narrow band around freqNominal. Sub-bin precision lets us
        // catch sub-Hz drift steps that real grid drift never produces.
        // FFT size large enough so 1 Hz ≈ 1 bin: with sr_resampled = 22050 and
        // FFT = 8192, bin width = 2.69 Hz — too coarse. Use FFT size = winSize
        // padded to next pow2 if needed, but only after windowing the slice;
        // peak interpolation gives sub-bin resolution.
        var fftSize = 1; while (fftSize < winSize) fftSize <<= 1;
        if (fftSize > 16384) fftSize = 16384;
        var freqTraj = new Float32Array(n);
        var winF = hann(winSize);
        var re = new Float32Array(fftSize);
        var im = new Float32Array(fftSize);
        var binNominal = freqNominal / sr * fftSize;
        var bandBins = Math.max(2, Math.round(0.6 / sr * fftSize)); // ±0.6 Hz around nominal
        var binLo = Math.max(1, Math.floor(binNominal - bandBins));
        var binHi = Math.min(fftSize / 2 - 1, Math.ceil(binNominal + bandBins));
        for (var w = 0; w < n; w++) {
            var off = w * hop;
            for (var i = 0; i < fftSize; i++) {
                re[i] = i < winSize ? samples[off + i] * winF[i] : 0;
                im[i] = 0;
            }
            fft(re, im);
            // Peak bin in [binLo, binHi]
            var peakBin = binLo, peakMag = 0;
            for (var b = binLo; b <= binHi; b++) {
                var m = re[b] * re[b] + im[b] * im[b];
                if (m > peakMag) { peakMag = m; peakBin = b; }
            }
            // Parabolic interpolation for sub-bin frequency.
            var ml = re[peakBin - 1] * re[peakBin - 1] + im[peakBin - 1] * im[peakBin - 1];
            var mc = peakMag;
            var mr = re[peakBin + 1] * re[peakBin + 1] + im[peakBin + 1] * im[peakBin + 1];
            ml = Math.sqrt(ml); mc = Math.sqrt(mc); mr = Math.sqrt(mr);
            var denom = ml - 2 * mc + mr;
            var delta = denom !== 0 ? 0.5 * (ml - mr) / denom : 0;
            if (delta < -1) delta = -1;
            if (delta >  1) delta =  1;
            freqTraj[w] = (peakBin + delta) * sr / fftSize;
        }

        // Median-smooth freq trajectory with a 5-tap window to suppress
        // single-bucket FFT-noise jitter.
        var freqSmooth = new Float32Array(n);
        for (var w = 0; w < n; w++) {
            var lo = Math.max(0, w - 2), hi = Math.min(n - 1, w + 2);
            var arr = [];
            for (var k = lo; k <= hi; k++) arr.push(freqTraj[k]);
            arr.sort(function(x, y) { return x - y; });
            freqSmooth[w] = arr[arr.length >> 1];
        }
        var freqScore = new Float32Array(n);
        for (var w = 2; w < n - 2; w++) {
            // Compare median of past 2 windows vs median of next 2 — splice
            // shows as a step that NO smoothing within ±2 windows can hide.
            var pastMed = (freqSmooth[w - 2] + freqSmooth[w - 1]) * 0.5;
            var nextMed = (freqSmooth[w + 1] + freqSmooth[w + 2]) * 0.5;
            freqScore[w] = Math.abs(nextMed - pastMed);
        }

        // Combine: per-bucket = max(phaseScore, freqScore × scale) weighted
        // by local ENF magnitude. Scale freqScore so its sigma is comparable
        // to phaseScore (frequency steps are typically 0.05-0.5 Hz, phase
        // residuals are 0.05-3 rad — same order of magnitude with no scale).
        //
        // Temporal alignment: phaseScore[w] is the difference between window
        // w-1 and w, so the splice that produced it is at the BOUNDARY of
        // those two windows, not at the center of window w. Assign the
        // score to bucket containing the midpoint of the two windows'
        // centers — i.e., (w-0.5)*hop + winSize/2. This was a 1-bucket
        // (~50 ms) misalignment that prevented ENF from co-locating with
        // flux/MFCC peaks on phase-reversal splices.
        var freqScoreScale = 1.0;
        for (var w = 1; w < n; w++) {
            var center = (w - 0.5) * hop + winSize / 2;
            var bi = Math.floor(center / bucketSamples);
            if (bi < 0) bi = 0;
            if (bi >= nBuckets) bi = nBuckets - 1;
            var weight = (mags[w] + mags[w - 1]) * 0.5 / (meanMag + 1e-9);
            if (weight > 1) weight = 1;
            var combined = Math.max(phaseScore[w], freqScore[w] * freqScoreScale) * weight;
            if (combined > out[bi]) out[bi] = combined;
        }
        return out;
    }

    /* ──────────────────────────────────────────────────────────────────────
     * Signal 5 — Pitch (F0) jump detection
     *
     * For voice content, the fundamental frequency F0 evolves smoothly
     * (within ~1 semitone per 50 ms in natural speech). A splice between
     * two voiced segments — even of the same speaker — produces an abrupt
     * F0 jump that natural speech doesn't make.
     *
     * We use a lightweight autocorrelation pitch tracker: for each 50 ms
     * frame, find the lag in the 50–400 Hz range with the highest
     * normalized autocorrelation. Score = absolute log-ratio of F0 between
     * adjacent voiced frames. Unvoiced frames contribute zero (don't penalize
     * non-voice).
     * ────────────────────────────────────────────────────────────────────── */
    function computeF0(samples, sr, bucketSamples) {
        var nBuckets = Math.ceil(samples.length / bucketSamples);
        var out = new Float32Array(nBuckets);

        var winSec = 0.05;
        var winSize = Math.floor(sr * winSec);
        var hop = winSize;
        var nWin = Math.floor((samples.length - winSize) / hop);
        if (nWin < 4) return out;

        // Autocorrelation lag bounds: 50 Hz → sr/50, 400 Hz → sr/400.
        var lagMin = Math.max(2, Math.floor(sr / 400));
        var lagMax = Math.min(winSize - 1, Math.floor(sr / 50));

        var f0 = new Float32Array(nWin);     // 0 = unvoiced
        var rmsArr = new Float32Array(nWin);

        for (var w = 0; w < nWin; w++) {
            var off = w * hop;
            // RMS gate — skip silent windows.
            var ssq = 0;
            for (var i = 0; i < winSize; i++) ssq += samples[off + i] * samples[off + i];
            var rms = Math.sqrt(ssq / winSize);
            rmsArr[w] = rms;
            if (rms < 0.005) continue;

            // Autocorrelation at integer lags, normalized.
            var bestLag = 0, bestR = 0;
            // r[0]
            var r0 = 0;
            for (var i = 0; i < winSize; i++) r0 += samples[off + i] * samples[off + i];
            if (r0 < 1e-9) continue;

            for (var lag = lagMin; lag <= lagMax; lag++) {
                var r = 0;
                for (var i = 0; i + lag < winSize; i++) {
                    r += samples[off + i] * samples[off + i + lag];
                }
                r /= r0;
                if (r > bestR) { bestR = r; bestLag = lag; }
            }
            // Voicedness: peak normalized autocorr > 0.4 → voiced.
            if (bestR > 0.4 && bestLag > 0) {
                f0[w] = sr / bestLag;
            }
        }

        // Score per voiced→voiced transition.
        for (var w = 1; w < nWin; w++) {
            if (f0[w] > 0 && f0[w - 1] > 0) {
                var ratio = Math.abs(Math.log(f0[w] / f0[w - 1]));
                // Suppress score in low-RMS windows (noise can produce
                // spurious lag peaks).
                var center = w * hop + winSize / 2;
                var bi = Math.floor(center / bucketSamples);
                if (bi >= nBuckets) bi = nBuckets - 1;
                if (ratio > out[bi]) out[bi] = ratio;
            }
        }
        return out;
    }

    /* ──────────────────────────────────────────────────────────────────────
     * Signal 6 — MFCC distance (timbre change)
     *
     * MFCCs (mel-frequency cepstral coefficients) are a compact
     * representation of timbre — what makes one mic, one room, one voice
     * sound different from another. Two consecutive MFCC frames in the same
     * recording sit near each other in MFCC space; a splice between two
     * different recordings has a step in this space.
     *
     * We compute a *very* lightweight MFCC: 26-band mel filterbank → log →
     * DCT keeping the first 13 coefficients (skip C0 which is just
     * loudness). Distance metric = L2 between adjacent frames, normalized
     * by the file's median frame-distance.
     * ────────────────────────────────────────────────────────────────────── */
    var _melCache = null;
    function makeMelFilterbank(sr, fftSize, nMels) {
        if (_melCache && _melCache.sr === sr && _melCache.fftSize === fftSize && _melCache.nMels === nMels) {
            return _melCache.fb;
        }
        var nBins = fftSize / 2 + 1;
        var fLo = 80, fHi = sr / 2;
        function hz2mel(f) { return 2595 * Math.log10(1 + f / 700); }
        function mel2hz(m) { return 700 * (Math.pow(10, m / 2595) - 1); }
        var melLo = hz2mel(fLo), melHi = hz2mel(fHi);
        var melPts = new Float32Array(nMels + 2);
        for (var i = 0; i < nMels + 2; i++) melPts[i] = mel2hz(melLo + (melHi - melLo) * i / (nMels + 1));
        var binPts = new Float32Array(nMels + 2);
        for (var i = 0; i < nMels + 2; i++) binPts[i] = Math.floor(melPts[i] * fftSize / sr);
        var fb = [];
        for (var m = 0; m < nMels; m++) {
            var row = new Float32Array(nBins);
            var a = binPts[m], b = binPts[m + 1], c = binPts[m + 2];
            for (var k = a; k < b; k++)  row[k] = (k - a) / Math.max(1, b - a);
            for (var k = b; k < c; k++)  row[k] = (c - k) / Math.max(1, c - b);
            fb.push(row);
        }
        _melCache = { sr: sr, fftSize: fftSize, nMels: nMels, fb: fb };
        return fb;
    }

    function computeMFCCDistance(samples, sr, bucketSamples) {
        var nBuckets = Math.ceil(samples.length / bucketSamples);
        var out = new Float32Array(nBuckets);

        var fftSize = 1024;
        var hop = 512;
        var nMels = 26;
        var nCoeff = 13;
        var nFrames = Math.floor((samples.length - fftSize) / hop);
        if (nFrames < 4) return out;

        var win = hann(fftSize);
        var fb = makeMelFilterbank(sr, fftSize, nMels);
        var re = new Float32Array(fftSize), im = new Float32Array(fftSize);
        var melE = new Float32Array(nMels);
        /* Те же лог-мел кадры нужны признаку «смена источника»: то же окно
           1024, тот же шаг 512, тот же банк. Считать их дважды стоит +38%
           времени анализа (замерено), а тул и так небыстрый — копим здесь
           и отдаём наружу. */
        var logMel = new Float32Array(nFrames * nMels);
        var prevMfcc = new Float32Array(nCoeff);
        var hasPrev = false;

        for (var k = 0; k < nFrames; k++) {
            var off = k * hop;
            for (var i = 0; i < fftSize; i++) { re[i] = samples[off + i] * win[i]; im[i] = 0; }
            fft(re, im);
            // Power spectrum, then mel-band energies, then log.
            for (var m = 0; m < nMels; m++) {
                var sum = 0;
                var row = fb[m];
                for (var b = 0; b < fftSize / 2 + 1; b++) {
                    var p = re[b] * re[b] + im[b] * im[b];
                    sum += p * row[b];
                }
                melE[m] = Math.log(sum + 1e-9);
                logMel[k * nMels + m] = melE[m];
            }
            // DCT-II keeping first nCoeff (skip C0).
            var mfcc = new Float32Array(nCoeff);
            for (var c = 1; c <= nCoeff; c++) {
                var s = 0;
                for (var m = 0; m < nMels; m++) {
                    s += melE[m] * Math.cos(Math.PI * c * (m + 0.5) / nMels);
                }
                mfcc[c - 1] = s;
            }
            // Distance to previous frame.
            if (hasPrev) {
                var d = 0;
                for (var c = 0; c < nCoeff; c++) {
                    var dd = mfcc[c] - prevMfcc[c];
                    d += dd * dd;
                }
                d = Math.sqrt(d);
                var center = k * hop + fftSize / 2;
                var bi = Math.floor(center / bucketSamples);
                if (bi >= nBuckets) bi = nBuckets - 1;
                if (d > out[bi]) out[bi] = d;
            }
            for (var c = 0; c < nCoeff; c++) prevMfcc[c] = mfcc[c];
            hasPrev = true;
        }
        out.__logMel = { data: logMel, nFrames: nFrames, nMels: nMels, hop: hop };
        return out;
    }

    /* ──────────────────────────────────────────────────────────────────────
     * Signal 7 — Bicoherence (third-order spectrum)
     *
     * Bicoherence is a normalised bispectrum that quantifies *quadratic
     * phase coupling* between frequency components. For a Gaussian random
     * process (and most natural sounds, which are well-modelled as
     * locally Gaussian) bicoherence is near zero. Splice transients are
     * non-linear events — they introduce phase-locked harmonics and
     * inter-modulation products that bicoherence detects directly.
     *
     * Practical formula per FFT frame:
     *   B(f1, f2) = X(f1) · X(f2) · X*(f1+f2)
     *   b²(f1,f2) = |B|² / (|X(f1)·X(f2)|² · |X(f1+f2)|²)
     *
     * We average b² over a triangle 200 Hz ≤ f1 ≤ f2 ≤ 2 kHz with
     * f1+f2 ≤ Nyquist. Each frame produces a single scalar; per-bucket
     * score = max scalar in that bucket. Splices show as peaks above the
     * file's baseline bicoherence.
     *
     * Cost: ~3000 ops/frame at FFT=256, ~600 frames per 10 s of audio
     * → ~2 ms/s — negligible.
     *
     * Reference: Nikias & Petropulu (1993), "Higher-Order Spectra Analysis".
     * ────────────────────────────────────────────────────────────────────── */
    function computeBicoherence(samples, sr, bucketSamples) {
        var nBuckets = Math.ceil(samples.length / bucketSamples);
        var out = new Float32Array(nBuckets);
        var fftSize = 256;
        var hop = fftSize >> 1;
        var nFrames = Math.floor((samples.length - fftSize) / hop);
        if (nFrames < 4) return out;

        var win = hann(fftSize);
        var nBins = fftSize >> 1;
        var binLo = Math.max(2, Math.round(200 / sr * fftSize));
        var binHi = Math.min(nBins - 2, Math.round(2000 / sr * fftSize));
        var re = new Float32Array(fftSize), im = new Float32Array(fftSize);
        var frameVal = new Float32Array(nFrames);

        for (var k = 0; k < nFrames; k++) {
            var off = k * hop;
            for (var i = 0; i < fftSize; i++) { re[i] = samples[off + i] * win[i]; im[i] = 0; }
            fft(re, im);
            var sum = 0, count = 0;
            for (var f1 = binLo; f1 <= binHi; f1++) {
                var r1 = re[f1], i1 = im[f1];
                var n1 = r1 * r1 + i1 * i1;
                if (n1 < 1e-12) continue;
                var f2max = Math.min(binHi, nBins - f1 - 1);
                for (var f2 = f1; f2 <= f2max; f2++) {
                    var r2 = re[f2], i2 = im[f2];
                    var r3 = re[f1 + f2], i3 = im[f1 + f2];
                    var n2 = r2 * r2 + i2 * i2;
                    var n3 = r3 * r3 + i3 * i3;
                    if (n2 < 1e-12 || n3 < 1e-12) continue;
                    // X(f1)·X(f2) = (r1+i·i1)(r2+i·i2)
                    var rA = r1 * r2 - i1 * i2;
                    var iA = r1 * i2 + i1 * r2;
                    // (X(f1)·X(f2))·X*(f1+f2)
                    var rB = rA * r3 + iA * i3;
                    var iB = iA * r3 - rA * i3;
                    var bispec2 = rB * rB + iB * iB;
                    // |X(f1)·X(f2)|² = n1·n2 (multiplicative property)
                    var norm = n1 * n2 * n3;
                    sum += bispec2 / norm;
                    count++;
                }
            }
            frameVal[k] = count > 0 ? sum / count : 0;
        }

        // Per-bucket = max frame bicoherence in that bucket. Splice peaks
        // dominate this max even when other frames have low bicoherence
        // (i.e. the splice is sharp not smeared).
        for (var k = 0; k < nFrames; k++) {
            var center = k * hop + (fftSize >> 1);
            var bi = Math.floor(center / bucketSamples);
            if (bi >= nBuckets) bi = nBuckets - 1;
            if (frameVal[k] > out[bi]) out[bi] = frameVal[k];
        }
        return out;
    }

    /* ──────────────────────────────────────────────────────────────────────
     * Signal 8 — RIR (Room Impulse Response) discontinuity
     *
     * The acoustic response of a room is a stable fingerprint — the same
     * decay time, the same early-reflection pattern. A splice between
     * recordings made in different rooms (or one room vs. dry close-mic'd
     * audio) produces a step-change in reverberance that survives equal
     * mic + equal gain — the signature noise-floor and MFCC sometimes miss.
     *
     * Full RIR estimation (deconvolution / Schroeder integration) needs an
     * impulse — which arbitrary recordings don't have. We use a robust
     * proxy: the *late-energy ratio* per high-energy onset. For each 1 s
     * window:
     *   1. Find the loudest 25 ms sub-frame (the onset surrogate).
     *   2. Measure the average 25 ms RMS in the 50–200 ms region after it.
     *   3. ratio[w] = late-RMS / peak-RMS.
     *
     * Reverberant rooms have ratio ≈ 0.05–0.20, dry close-mic'd audio has
     * ratio ≈ 0.001–0.03. A splice between regimes shows as a step in
     * ratio[w] across adjacent windows — same kind of change-point detection
     * as the noise-floor signal but on a different, complementary feature.
     *
     * If no clear onset is found in any window (e.g. continuous tone, white
     * noise) the signal returns all zeros and its weight is redistributed.
     * ────────────────────────────────────────────────────────────────────── */
    function computeRIR(samples, sr, bucketSamples) {
        var nBuckets = Math.ceil(samples.length / bucketSamples);
        var out = new Float32Array(nBuckets);
        out.__present = false;

        var winSec = 1.0;
        var winSize = Math.floor(sr * winSec);
        var hop = winSize >> 1;        // 0.5 s hop
        var nWin = Math.floor((samples.length - winSize) / hop);
        if (nWin < 4) return out;

        var subSize = Math.max(64, Math.floor(sr * 0.025)); // 25 ms
        var nSubs = Math.floor(winSize / subSize);
        var ratios = new Float32Array(nWin);
        var validCount = 0;

        for (var w = 0; w < nWin; w++) {
            var off = w * hop;
            var rmsArr = new Float32Array(nSubs);
            var globalPeak = 0;
            for (var s = 0; s < nSubs; s++) {
                var sum = 0;
                var subOff = off + s * subSize;
                for (var i = 0; i < subSize; i++) {
                    var v = samples[subOff + i];
                    sum += v * v;
                }
                rmsArr[s] = Math.sqrt(sum / subSize);
                if (rmsArr[s] > globalPeak) globalPeak = rmsArr[s];
            }
            // Need a clear onset: peak ≥ 4× the median sub-frame RMS.
            var sortedRms = Array.from(rmsArr).sort(function(a, b) { return a - b; });
            var medRms = sortedRms[sortedRms.length >> 1];
            if (medRms < 1e-4 || globalPeak < medRms * 4) continue;
            // Find peak sub-frame
            var peakIdx = 0;
            for (var s = 0; s < nSubs; s++) if (rmsArr[s] === globalPeak) { peakIdx = s; break; }
            // Late region: 50 ms after peak (≈ 2 sub-frames) up to 200 ms
            // (≈ 8 sub-frames). Skip if not enough room.
            var postStart = peakIdx + 2;
            var postEnd = Math.min(nSubs - 1, peakIdx + 8);
            if (postEnd <= postStart) continue;
            var postSum = 0, postN = 0;
            for (var s = postStart; s <= postEnd; s++) { postSum += rmsArr[s]; postN++; }
            var postAvg = postN > 0 ? postSum / postN : 0;
            ratios[w] = postAvg / (globalPeak + 1e-9);
            validCount++;
        }

        if (validCount < 3) return out;
        out.__present = true;

        // Score per window: |Δratio| between adjacent VALID windows (skip
        // pairs where either side has no onset). Map to bucket grid.
        for (var w = 1; w < nWin; w++) {
            if (ratios[w] === 0 || ratios[w - 1] === 0) continue;
            var d = Math.abs(ratios[w] - ratios[w - 1]);
            var center = w * hop + (winSize >> 1);
            var bi = Math.floor(center / bucketSamples);
            if (bi >= nBuckets) bi = nBuckets - 1;
            if (d > out[bi]) out[bi] = d;
        }
        return out;
    }

    /* ──────────────────────────────────────────────────────────────────────
     * Score normalization — convert raw signal arrays to per-bucket sigma
     * scores in [0, 1], where 1 = exceeds μ + sigmaK·σ.
     *
     * Why per-signal normalization (not global): each signal has a different
     * absolute scale (flux is in spectral units, F0 in log-Hz, ENF in
     * radians, MFCC in cepstral L2). Normalizing each independently to the
     * file's own statistics makes them weight-comparable in fusion.
     * ────────────────────────────────────────────────────────────────────── */
    /* ──────────────────────────────────────────────────────────────────────
     * Signal 9 — Source contrast (long-window timbre)
     *
     * Все восемь сигналов выше смотрят на МГНОВЕНИЕ: кадр против соседнего
     * кадра, 11–100 мс. На настоящей речи мгновенные перепады происходят
     * непрерывно — каждый взрывной согласный, каждая пауза, — поэтому на
     * живых записях они не разделяют смонтированное и непрерывное.
     *
     * Замер 2026-08-21 на 4.7 минутах непрерывной речи (наша озвучка,
     * человеческая запись 2008 г., акустическая запись 1920-х — последняя
     * непрерывна физически, монтажа тогда не существовало) и 14 склейках
     * из того же материала:
     *
     *   восемь сигналов, порог 0.50 → находок 3/8, ЛОЖНЫХ 14.0 в минуту;
     *   этот признак,     порог 0.45 → находок 6/14, ложных 0.0 в минуту.
     *
     * Вопрос ставится иначе, по-криминалистически: «отличается ли то, что
     * звучало ДВЕ СЕКУНДЫ ДО, от того, что звучит ДВЕ СЕКУНДЫ ПОСЛЕ?».
     * Смена микрофона, помещения, дубля или диктора держится секундами;
     * речевые перепады — нет. Поэтому признак ловит ровно смену ИСТОЧНИКА
     * (замерено 0.46–1.20 при максимуме 0.437 на непрерывных) и честно
     * молчит на монтаже внутри одной записи (0.07–0.14) — там менять нечему.
     *
     * ⚠️ Уровень вычитается из каждого вектора (среднее по полосам). Без
     * этого косинус на лог-мел векторах ловит не тембр, а ПАУЗЫ: в тишине
     * все полосы уходят в логарифм нуля, направление вектора рушится, и
     * непрерывная запись с паузой выглядит сменой источника (замерено: 1.52
     * на файле, где склейки нет).
     *
     * ⛔ Контраст шумового пола (10-й процентиль энергии слева/справа)
     * проверен и ОТВЕРГНУТ: 30–68 ложных в минуту на любом пороге — на речи
     * он скачет от пауз. Объединение с ним только портит тембровый признак.
     * ────────────────────────────────────────────────────────────────────── */
    function computeSourceContrast(logMel, sr, bucketSamples, nSamples) {
        var nBuckets = Math.ceil(nSamples / bucketSamples);
        var out = new Float32Array(nBuckets);
        if (!logMel || nSamples < sr * 5) return out;

        var mel = logMel.data, nf = logMel.nFrames, NM = logMel.nMels, HOP = logMel.hop;
        var W = Math.round(2.0 * sr / HOP);          // окно контраста — 2 с в каждую сторону
        if (nf < 2 * W + 4) return out;

        /* ⚠️ Тембр считается ТОЛЬКО по речевым кадрам, тишина внутри окна
           игнорируется. Иначе признак ловит не смену источника, а конец
           речи: замер на 11 минутах непрерывных записей дал ровно один
           выброс 1.16 при остальных 0.12–0.35, и это был хвост записи, где
           диктор замолчал. Заслон «пропускать точки, где одна сторона тиха»
           не спасает — контраст велик на всём участке затухания, а не в
           одной точке. Сравнивать надо голос с голосом.

           Уровень кадра — самая громкая полоса: среднее логарифма мощности
           по полосам задавлено тихими верхними полосами и к речи почти
           слепо (проверено, с ним заслон резал настоящие швы). */
        var lvl = new Float64Array(nf);
        for (var k = 0; k < nf; k++) {
            var peak = -1e9;
            for (var m = 0; m < NM; m++) { var v = mel[k * NM + m]; if (v > peak) peak = v; }
            lvl[k] = peak;
        }
        var sorted = Array.prototype.slice.call(lvl).sort(function (a, b) { return a - b; });
        var loudRef = sorted[Math.floor(nf * 0.95)];
        var SPEECH_DROP = 6.0;      // ниже этого от верхнего процентиля — не речь
        var MIN_FRAMES = Math.max(8, Math.round(W * 0.30));

        // Префиксные суммы ТОЛЬКО по речевым кадрам + их счётчик.
        var pre = new Float64Array((nf + 1) * NM);
        var cnt = new Int32Array(nf + 1);
        for (var k = 0; k < nf; k++) {
            var isSpeech = lvl[k] > loudRef - SPEECH_DROP ? 1 : 0;
            cnt[k + 1] = cnt[k] + isSpeech;
            for (var m = 0; m < NM; m++) {
                pre[(k + 1) * NM + m] = pre[k * NM + m] + (isSpeech ? mel[k * NM + m] : 0);
            }
        }

        var A = new Float64Array(NM), B = new Float64Array(NM);
        for (var k = W; k < nf - W; k++) {
            var nA = cnt[k] - cnt[k - W], nB = cnt[k + W] - cnt[k];
            if (nA < MIN_FRAMES || nB < MIN_FRAMES) continue;
            var ma = 0, mb = 0;
            for (var m = 0; m < NM; m++) {
                A[m] = (pre[k * NM + m] - pre[(k - W) * NM + m]) / nA;
                B[m] = (pre[(k + W) * NM + m] - pre[k * NM + m]) / nB;
                ma += A[m]; mb += B[m];
            }
            ma /= NM; mb /= NM;
            /* Уровень вычитается из каждого вектора: без этого косинус ловит
               громкость, а не тембр. */
            var dot = 0, na = 0, nb = 0;
            for (var m = 0; m < NM; m++) {
                var av = A[m] - ma, bv = B[m] - mb;
                dot += av * bv; na += av * av; nb += bv * bv;
            }
            var contrast = 1 - dot / (Math.sqrt(na * nb) + 1e-12);
            var bi = Math.floor(k * HOP / bucketSamples);
            if (bi >= nBuckets) bi = nBuckets - 1;
            if (contrast > out[bi]) out[bi] = contrast;
        }
        return out;
    }

    function normalizeSignal(arr, sigmaK) {
        // Robust per-file normalization. Splice peaks are by definition
        // outliers in the per-bucket signal array — but mean+std statistics
        // are *dominated by those very outliers*, which inflates the
        // threshold so the splice barely passes its own gate (the
        // "outlier-eats-its-own-threshold" bug).
        //
        // Fix: compute mean/std on the BOTTOM 75% of non-zero values only.
        // This is robust median-style statistics that excludes the splice
        // peak from the threshold derivation, then thresholds the FULL
        // array (including the peak) against that robust threshold.
        var nz = [];
        for (var i = 0; i < arr.length; i++) if (arr[i] > 0) nz.push(arr[i]);
        if (nz.length < 4) return new Float32Array(arr.length);
        var sorted = nz.slice().sort(function(a, b) { return a - b; });
        // Bottom 75% (cap at 4 minimum)
        var trimEnd = Math.max(4, Math.floor(sorted.length * 0.75));
        var baseline = sorted.slice(0, trimEnd);
        var stat = meanStd(baseline);
        var threshold = stat.mean + sigmaK * stat.std;
        var out = new Float32Array(arr.length);
        // Sigmoid-ish span uses 2× the BASELINE std so a peak at 4σ above
        // baseline mean → t = (4σ - kσ) / 2σ = (4-k)/2. With sigmaK=2.5,
        // that's t=0.75. With sigmaK=3.0, t=0.5. Both well-distinguished.
        var span = 2 * stat.std + 1e-9;
        for (var i = 0; i < arr.length; i++) {
            if (arr[i] <= 0) continue;
            var t = (arr[i] - threshold) / span;
            if (t < 0) out[i] = 0;
            else if (t > 1) out[i] = 1;
            else out[i] = t;
        }
        out.__threshold = threshold;
        out.__mean = stat.mean;
        out.__std = stat.std;
        return out;
    }

    /* ──────────────────────────────────────────────────────────────────────
     * Fusion — weighted sum of per-signal scores.
     *
     * Signals that are not "present" in this recording (e.g. ENF for
     * outdoor / digital sources, RIR for continuous tones) have their weight
     * redistributed proportionally across the remaining present signals.
     * Without redistribution, recordings with fewer applicable signals would
     * always score lower for the same splice severity.
     * ────────────────────────────────────────────────────────────────────── */
    function fuse(signals, weights, presence) {
        var w = Object.assign({}, weights);
        var keys = ['flux', 'noise', 'click', 'enf', 'pitch', 'mfcc', 'bicoh', 'rir'];
        var totalAbsent = 0;
        for (var i = 0; i < keys.length; i++) {
            var k = keys[i];
            if (presence[k] === false) { totalAbsent += w[k]; w[k] = 0; }
        }
        if (totalAbsent > 0) {
            var remainingSum = 0;
            for (var i = 0; i < keys.length; i++) remainingSum += w[keys[i]];
            if (remainingSum > 0) {
                var scale = (remainingSum + totalAbsent) / remainingSum;
                for (var i = 0; i < keys.length; i++) w[keys[i]] *= scale;
            }
        }
        var n = signals.flux.length;
        var fused = new Float32Array(n);
        for (var i = 0; i < n; i++) {
            fused[i] = signals.flux[i]  * w.flux
                     + signals.noise[i] * w.noise
                     + signals.click[i] * w.click
                     + signals.enf[i]   * w.enf
                     + signals.pitch[i] * w.pitch
                     + signals.mfcc[i]  * w.mfcc
                     + signals.bicoh[i] * w.bicoh
                     + signals.rir[i]   * w.rir;
        }
        fused.__weights = w;
        return fused;
    }

    /* ──────────────────────────────────────────────────────────────────────
     * Candidate extraction — find local maxima in the fused signal that
     * exceed minScore, then merge adjacent ones within mergeMs.
     *
     * Different signals report at slightly different temporal grids (ENF
     * uses 100 ms hops, MFCC 25 ms, etc.) so a single splice can produce
     * a "shoulder" of activity across 5-10 buckets rather than a single
     * peak. We pre-pool the fused signal with a neighborhood max over
     * ±2 buckets (±100 ms) — this aligns the per-signal contributions
     * before peak-finding without losing precision (the original peak
     * bucket still wins among the merged group).
     * ────────────────────────────────────────────────────────────────────── */
    function extractSplices(fused, signals, bucketMs, mergeMs, minScore) {
        var nBuckets = fused.length;
        // Neighborhood evidence-pool: pooled[i] rewards coherent activity
        // across ±2 buckets without inflating isolated single-bucket peaks.
        // Formula: max + 0.30·second_max + 0.15·third_max, clipped to 1.0.
        // - Single-peak case: pooled[i] ≈ peak (no spurious boost).
        // - Two adjacent signals at neighbouring buckets: pooled boosts by
        //   ~0.3× of the secondary signal's contribution.
        // - Three+ signals across a 250 ms window: boosted by both 2nd & 3rd.
        // The boost is capped at 1.0 so a saturated peak can't go above
        // the natural fused-score scale.
        var pooled = new Float32Array(nBuckets);
        for (var i = 0; i < nBuckets; i++) {
            var lo = Math.max(0, i - 2), hi = Math.min(nBuckets - 1, i + 2);
            var sortedNb = [];
            for (var k = lo; k <= hi; k++) sortedNb.push(fused[k]);
            sortedNb.sort(function(a, b) { return b - a; });
            var combined = sortedNb[0]
                         + 0.30 * (sortedNb[1] || 0)
                         + 0.15 * (sortedNb[2] || 0);
            pooled[i] = Math.min(1.0, combined);
        }

        // Raw-score gate: a bucket only qualifies if its un-pooled fused
        // score is high enough to begin with. Without this, clean files
        // with natural per-signal fluctuations (pink noise, music chords)
        // can have 5 mild-firing buckets that pool into a spurious "splice".
        var rawGate = minScore * (DEFAULTS.minRawScoreFraction || 0.6);
        var peakRadius = 2;

        // Distinctiveness gate: real splices stand out from the file's own
        // baseline activity. Random fluctuations in white-noise / chaotic
        // signals can produce comparable peaks throughout — the splice
        // peak/(75th percentile) ratio for a real splice is typically
        // ≥4× while for noise it's only 1-2×.
        //
        // We use 75th percentile (not median) of the FULL pooled array
        // (including zero buckets — silence is the natural baseline) so
        // that "bulk activity floor" of a chaotic file like white noise
        // is captured, while a clean tone (mostly zero pool, one peak)
        // has p75 ≈ 0 and the gate becomes trivially passable.
        var poolSorted = Array.from(pooled);
        poolSorted.sort(function(a, b) { return a - b; });
        var p75 = poolSorted.length > 0
            ? poolSorted[Math.floor(poolSorted.length * 0.75)]
            : 0;
        // Gate scales with p75 (at least 0.04 absolute floor so trivially
        // quiet files don't auto-pass on tiny noise). Multiplier of 1.25 is
        // a calibration sweet spot: 1.6 dropped detection 90→70%, 1.0 left
        // FP at 14%, 1.25 keeps detection above 80% with FP ~10%.
        var distinctivenessGate = Math.max(0.04, p75 * 1.25);

        // File-wide "fire count" per signal — used by the evidence filter
        // to distinguish *isolated* gold-standard peaks (real splices)
        // from broadly-active signals (noisy chaotic content). A signal
        // that fires in many buckets across the file is unreliable
        // evidence even when its peak value is high.
        function countFires(arr, threshold) {
            var n = 0;
            for (var k = 0; k < arr.length; k++) if (arr[k] >= threshold) n++;
            return n;
        }
        var enfFireCount   = countFires(signals.enf,   0.4);
        var noiseFireCount = countFires(signals.noise, 0.5);

        // Plateau-aware peak detection: a bucket is a peak if its pooled
        // score is >= every value in [i-peakRadius, i+peakRadius] AND it
        // wins the tie with adjacent equal-pooled buckets by having the
        // higher *raw* fused score (or, if tied there too, the earliest
        // bucket — same merging logic happens later anyway).
        //
        // GOLD-STANDARD RELAXATION: a bucket where ENF fires strongly
        // (sig.enf ≥ 0.7) AND ENF is rare across the file (≤ 5 buckets
        // firing) gets relaxed minScore + distinctiveness thresholds —
        // ENF phase/freq breaks are forensic gold-standard (Grigoras
        // 2007), so when they're clearly present and isolated we accept
        // lower aggregate fused scores. Same for noise-floor when it's
        // saturated and isolated (catches "same source, different mic
        // noise" splices that fire only the noise signal).
        var raw = [];
        for (var i = 0; i < nBuckets; i++) {
            var enfHere   = signals.enf[i]   || 0;
            var noiseHere = signals.noise[i] || 0;
            var mfccHere  = signals.mfcc[i]  || 0;
            var enfRare   = enfFireCount   <= 5;
            var noiseRare = noiseFireCount <= 3;
            var enfGold   = enfHere   >= 0.7  && enfRare;
            // Noise gold-standard requires mfcc corroboration: a real
            // noise-floor splice (different mic / different gain) ALSO
            // changes the timbre signature (mfcc) at the seam. Voice
            // vibrato and sweep fire only noise, no mfcc — this filter
            // rejects them as splices.
            var noiseGold = noiseHere >= 0.85 && noiseRare && mfccHere >= 0.3;

            var minScoreThis = (enfGold || noiseGold) ? Math.min(minScore, 0.30) : minScore;
            var distGateThis = (enfGold || noiseGold) ? Math.max(0.04, p75 * 0.7)  : distinctivenessGate;

            if (pooled[i] < minScoreThis) continue;
            if (pooled[i] < distGateThis) continue;

            var isPeak = true;
            var lo = Math.max(0, i - peakRadius), hi = Math.min(nBuckets - 1, i + peakRadius);
            for (var k = lo; k <= hi; k++) {
                if (k === i) continue;
                if (pooled[k] > pooled[i]) { isPeak = false; break; }
                if (pooled[k] === pooled[i] && k < i) { isPeak = false; break; }
            }
            if (!isPeak) continue;
            var bestIdx = i, bestVal = fused[i];
            for (var k = lo; k <= hi; k++) {
                if (fused[k] > bestVal) { bestVal = fused[k]; bestIdx = k; }
            }
            if (!enfGold && !noiseGold && bestVal < rawGate) continue;
            raw.push({ idx: bestIdx, score: pooled[i] });
        }

        // Merge candidates within mergeMs (group adjacent peaks into one).
        raw.sort(function(a, b) { return a.idx - b.idx; });
        var mergeBuckets = Math.ceil(mergeMs / bucketMs);
        var merged = [];
        for (var i = 0; i < raw.length; i++) {
            if (merged.length > 0 && raw[i].idx - merged[merged.length - 1].idx <= mergeBuckets) {
                // Keep the higher-scoring of the two.
                if (raw[i].score > merged[merged.length - 1].score) {
                    merged[merged.length - 1] = raw[i];
                }
            } else {
                merged.push(raw[i]);
            }
        }

        // Build full splice records with per-signal contributions for "why".
        // Final filter: a splice must show evidence in at least THREE distinct
        // signals (each at ≥ 0.2 σ-score) OR have a single signal at ≥ 0.85
        // accompanied by ≥1 corroborating signal at ≥ 0.4. This filters the
        // 2-signal accidental-pass case (voice-like vibrato firing pitch+noise
        // by coincidence) while preserving real splices, which by their nature
        // disturb multiple acoustic dimensions.
        var out = [];
        for (var i = 0; i < merged.length; i++) {
            var idx = merged[i].idx;
            var sig = {
                flux:  signals.flux[idx],
                noise: signals.noise[idx],
                click: signals.click[idx],
                enf:   signals.enf[idx],
                pitch: signals.pitch[idx],
                mfcc:  signals.mfcc[idx],
                bicoh: signals.bicoh[idx],
                rir:   signals.rir[idx],
            };
            var ranked = Object.keys(sig).map(function(k) {
                return { k: k, v: sig[k] };
            }).filter(function(x) { return x.v > 0.2; }).sort(function(a, b) {
                return b.v - a.v;
            });
            // Final evidence filter — accept if ANY of:
            //   (a) ≥3 distinct signals fired (multi-domain corroboration)
            //   (b) ENF gold-standard alone (Grigoras 2007: forensic-grade)
            //       AND ENF is rare in the file (not jittering everywhere)
            //   (c) flux + a corroborator both ≥0.7 (sharp spectral
            //       discontinuity at the same bucket as another signal)
            //   (d) noise-saturated isolated peak — same source / different
            //       mic (or different gain) splices fire only the noise-
            //       floor signal. Accept when noise ≥0.85 AND that peak
            //       is rare in the file (≤3 buckets firing). This catches
            //       e.g. an interview where the editor cut between two
            //       takes recorded on slightly different mics.
            var nFiring = ranked.length;
            var hasEnfGold = (sig.enf >= 0.7) && (enfFireCount <= 5);
            var hasFluxCorroborator =
                (sig.flux >= 0.7) &&
                ranked.some(function(x) { return x.k !== 'flux' && x.v >= 0.7; });
            var hasNoiseGold = (sig.noise >= 0.85) && (noiseFireCount <= 3) && (sig.mfcc >= 0.3);
            if (!hasEnfGold && !hasFluxCorroborator && !hasNoiseGold && nFiring < 3) continue;

            var explain = [];
            for (var r = 0; r < ranked.length; r++) explain.push(ranked[r].k);
            out.push({
                time: idx * bucketMs / 1000,
                score: merged[i].score,
                sigSig: sig,
                explain: explain,
            });
        }
        return out;
    }

    /* ──────────────────────────────────────────────────────────────────────
     * Verdict — overall integrity assessment from splice list + fused stats.
     * ────────────────────────────────────────────────────────────────────── */
    /* Плотность, выше которой находки перестают быть заключением. Число не
       выдумано: ровно при нём интерфейс тула уже показывает предупреждение
       «скорее всего переобнаружение» и сам поднимает фильтр уверенности.
       Держим ОДНО значение на движок и страницу — раньше их было два, и они
       противоречили друг другу: вердикт «подделка» ставился при четырёх
       склейках независимо от длины, то есть на уровне, который тул тут же
       помечал как ненадёжный. */
    var OVERDETECT_PER_MIN = 3;

    function verdictOf(splices, fused, duration) {
        // Aggregate score: max fused × log(1 + count), normalized.
        var maxFused = 0;
        for (var i = 0; i < fused.length; i++) if (fused[i] > maxFused) maxFused = fused[i];
        var n = splices.length;
        var density = n / Math.max(1, duration / 60); // splices per minute
        var raw = Math.min(1, maxFused) * (1 + Math.log(1 + n)) * 0.4 + Math.min(1, density / 4) * 0.3;
        var pct = Math.round(Math.min(100, raw * 100));

        /* Находки «смена источника» проверены отдельно: НОЛЬ ложных на 21
           минуте непрерывных записей. Они — основание для вердикта даже при
           высокой плотности остальных кандидатов. */
        var srcHits = 0;
        for (var i = 0; i < n; i++) {
            var ex = splices[i] && splices[i].explain;
            if (ex && ex.indexOf('source') >= 0) srcHits++;
        }
        var overDetected = density > OVERDETECT_PER_MIN;

        var verdict;
        if (n === 0) verdict = 'clean';
        else if (srcHits > 0) verdict = 'tampered';
        else if (overDetected) {
            /* ⚠️ Замер 2026-08-21: на настоящей речи восемь мгновенных сигналов
               дают 14 ложных склеек в минуту, а распределения у непрерывного и
               смонтированного СОВПАДАЮТ (максимум 0.560 против 0.554) — никакой
               порог их не разделяет. Наша собственная непрерывная запись 145 с
               получала «подделка, 100%». Здесь заключение не выносится: список
               кандидатов остаётся, но обвинения нет. */
            verdict = 'inconclusive';
        }
        else if (n === 1 && maxFused < 0.75) verdict = 'one_splice';
        else if (n <= 3 && maxFused < 0.85) verdict = 'multiple_splices';
        else verdict = 'tampered';

        /* При невынесенном заключении счёт не показывает уверенности: он и так
           был счётчиком находок (формула насыщается уже на нескольких), а рядом
           со словами «судить нельзя» большое число просто вводит в заблуждение. */
        if (verdict === 'inconclusive') pct = 0;

        return { verdict: verdict, score: pct, rawScore: Math.round(raw * 100) / 100,
                 overDetected: overDetected, density: Math.round(density * 10) / 10 };
    }

    /* ──────────────────────────────────────────────────────────────────────
     * Public — analyze(audioBuffer, opts)
     * ────────────────────────────────────────────────────────────────────── */
    async function analyze(audioBuffer, opts) {
        opts = Object.assign({}, DEFAULTS, opts || {});
        opts.sigmaK = Object.assign({}, DEFAULTS.sigmaK, opts.sigmaK || {});
        opts.weights = Object.assign({}, DEFAULTS.weights, opts.weights || {});

        var onProgress = opts.onProgress || function() {};
        var srIn = audioBuffer.sampleRate;
        var monoIn = downmixToMono(audioBuffer);

        onProgress('downmix', 5);

        // Truncate gracefully if absurdly long.
        var maxSamples = opts.maxAnalyzeSec * srIn;
        if (monoIn.length > maxSamples) monoIn = monoIn.subarray(0, maxSamples);

        // Resample to target rate to bound FFT cost. 2026-06-04 fix: was
        // resampleLinear which had no anti-aliasing — frequencies above the
        // target Nyquist folded back into the analysis band and produced
        // spurious spectral-flux events (false-positive splice claims).
        // resampleHQ is Kaiser-windowed sinc with baked-in LP at the new
        // Nyquist; analyze() is already async so await is free.
        var sr = opts.targetSampleRate;
        var samples = srIn > sr * 1.5 ? await resampleHQ(monoIn, srIn, sr) : monoIn;
        if (samples === monoIn) sr = srIn;

        var bucketSamples = Math.floor(sr * opts.bucketMs / 1000);
        var duration = monoIn.length / srIn;

        // Music vs speech classification — heuristic that adapts the
        // detector for content type. Music transients (drum hits,
        // attacks, beat changes) fire flux/click/MFCC at every
        // structural event; speech has them only at splice points.
        // For music-like content we double the sigmaK on transient-
        // sensitive signals so only severely-stronger-than-baseline
        // events qualify.
        //
        // Only apply for sufficiently long content (> 15s). Short
        // synthetic test inputs (4s pure sines, etc.) misclassify as
        // "music" because they ARE pure tonal content — but they're
        // also our test cases. Real music tracks are minutes-long.
        var content = classifyContent(samples, sr);
        var applyMusicAdjustment = content.isMusicLike && duration > 15;
        if (applyMusicAdjustment) {
            opts.sigmaK = Object.assign({}, opts.sigmaK, {
                flux:  opts.sigmaK.flux  * 2.0,
                click: opts.sigmaK.click * 1.5,
                pitch: opts.sigmaK.pitch * 1.8,
                mfcc:  opts.sigmaK.mfcc  * 1.5,
                bicoh: opts.sigmaK.bicoh * 1.5,
            });
        }

        // Yield to UI between heavy passes — none of these are async, but a
        // setTimeout(0) lets the spinner repaint between stages.
        function yieldUi() { return new Promise(function(r) { setTimeout(r, 0); }); }

        onProgress('flux', 10);
        var rawFlux = computeSpectralFlux(samples, sr, bucketSamples);
        await yieldUi();

        onProgress('noise', 22);
        var rawNoise = computeNoiseFloor(samples, sr, bucketSamples);
        await yieldUi();

        onProgress('click', 32);
        var rawClick = computeClicks(samples, sr, bucketSamples);
        await yieldUi();

        onProgress('enf', 45);
        var rawEnf = computeENF(samples, sr, bucketSamples);
        await yieldUi();

        onProgress('pitch', 58);
        var rawPitch = computeF0(samples, sr, bucketSamples);
        await yieldUi();

        onProgress('mfcc', 70);
        var rawMfcc = computeMFCCDistance(samples, sr, bucketSamples);
        await yieldUi();

        onProgress('bicoh', 82);
        var rawBicoh = computeBicoherence(samples, sr, bucketSamples);
        await yieldUi();

        onProgress('rir', 92);
        var rawRir = computeRIR(samples, sr, bucketSamples);
        var rawSource = computeSourceContrast(rawMfcc.__logMel, sr, bucketSamples, samples.length);
        await yieldUi();

        onProgress('fuse', 96);
        var signals = {
            flux:  normalizeSignal(rawFlux,  opts.sigmaK.flux),
            noise: normalizeSignal(rawNoise, opts.sigmaK.noise),
            click: normalizeSignal(rawClick, opts.sigmaK.click),
            enf:   normalizeSignal(rawEnf,   3.0),
            pitch: normalizeSignal(rawPitch, opts.sigmaK.pitch),
            mfcc:  normalizeSignal(rawMfcc,  opts.sigmaK.mfcc),
            bicoh: normalizeSignal(rawBicoh, opts.sigmaK.bicoh),
            rir:   normalizeSignal(rawRir,   opts.sigmaK.rir),
        };
        var presence = {
            flux:  true,
            noise: true,
            click: true,
            enf:   !!rawEnf.__present,
            pitch: true,
            mfcc:  true,
            bicoh: true,
            rir:   !!rawRir.__present,
        };
        var fused = fuse(signals, opts.weights, presence);
        signals.source = rawSource;   // сырой контраст: свой порог, вне суммы
        signals.fused = fused;

        var splices = extractSplices(fused, signals, opts.bucketMs, opts.mergeMs, opts.minScore);

        /* Находки смены источника добавляются ОТДЕЛЬНО от взвешенной суммы.
           Порог 0.45 выбран замером: на 4.7 минутах непрерывной речи он даёт
           НОЛЬ срабатываний (максимум на непрерывных 0.437), а на склейках
           разных записей значение 0.46–1.20. Складывать его с остальными
           восемью нельзя: их сумма на живой речи и так шумит 14 раз в минуту,
           и признак с нулём ложных в ней растворился бы. */
        /* Порог 0.40 — замер, а не вкус. Непрерывные настоящие записи дают не
           выше 0.33: 34 окна по 20 с (11.3 мин) из трёх источников И три
           целых файла (145, 198, 226 с) — пик 0.311–0.329. Соединение разных
           записей даёт 0.44–0.91. Порог 0.35 ловил бы то же самое, но с
           запасом 6% вместо 21%; лишних находок он не давал, а вот запас
           для криминалистического заключения дороже. */
        var SOURCE_MIN = 0.40;
        var srcFound = [];
        for (var si = 1; si < rawSource.length - 1; si++) {
            if (rawSource[si] < SOURCE_MIN) continue;
            if (rawSource[si] < rawSource[si - 1] || rawSource[si] < rawSource[si + 1]) continue;
            var st = si * opts.bucketMs / 1000;
            if (srcFound.some(function (p) { return Math.abs(p.time - st) < opts.mergeMs / 1000; })) continue;
            if (splices.some(function (p) { return Math.abs(p.time - st) < opts.mergeMs / 1000; })) continue;
            srcFound.push({
                time: st,
                score: Math.min(1, 0.80 + 0.40 * (rawSource[si] - SOURCE_MIN)),
                sigSig: { source: rawSource[si] },
                explain: ['source'],
            });
        }
        if (srcFound.length) {
            splices = splices.concat(srcFound).sort(function (a, b) { return a.time - b.time; });
        }

        // Edge artifact filter — STFT and percentile-RMS estimators have
        // boundary effects at the start and end of any signal. Splice
        // candidates within 0.3 s of either edge are very likely
        // detector artifacts (zero-padding leakage, partial windows)
        // rather than real cuts. Real-world editors don't splice in
        // the first 300 ms of a recording either — the lead-in is
        // almost always continuous capture.
        var edgeGuardSec = 0.3;
        if (duration > 1.0) {
            splices = splices.filter(function(sp) {
                return sp.time >= edgeGuardSec && sp.time <= (duration - edgeGuardSec);
            });
        }
        var v = verdictOf(splices, fused, duration);

        onProgress('done', 100);

        return {
            splices: splices,
            signals: signals,
            bucketMs: opts.bucketMs,
            duration: duration,
            sampleRate: srIn,
            verdict: v.verdict,
            score: v.score,
            rawScore: v.rawScore,
            overDetected: v.overDetected,     // одна истина для вердикта и баннера
            spliceDensity: v.density,
            enfPresent: !!rawEnf.__present,
            enfFreq: rawEnf.__freq || 0,
            rirPresent: !!rawRir.__present,
            isMusicLike: !!applyMusicAdjustment,
            content: content,
            presence: presence,
            thresholds: {
                flux:  signals.flux.__threshold,
                noise: signals.noise.__threshold,
                click: signals.click.__threshold,
                enf:   signals.enf.__threshold,
                pitch: signals.pitch.__threshold,
                mfcc:  signals.mfcc.__threshold,
                bicoh: signals.bicoh.__threshold,
                rir:   signals.rir.__threshold,
            },
            weights: fused.__weights,
            minScore: opts.minScore,
            engineVersion: __VERSION,
        };
    }

    window.SpliceDetector = {
        VERSION: __VERSION,
        analyze: analyze,
        _downmixToMono: downmixToMono,
        _computeSpectralFlux: computeSpectralFlux,
        _computeNoiseFloor: computeNoiseFloor,
        _computeClicks: computeClicks,
        _computeENF: computeENF,
        _computeSourceContrast: computeSourceContrast,
        _computeF0: computeF0,
        _computeMFCCDistance: computeMFCCDistance,
        _computeBicoherence: computeBicoherence,
        _computeRIR: computeRIR,
        _normalizeSignal: normalizeSignal,
        __isStub: false,
    };
})();
